import { ActionRegistry } from "../../../ServiceDesk/sdServices/agent-action/registry-contract";
import {
  ActionTriggerLink,
  ActionTriggerOptions,
  RightRequirement
} from "../../../ServiceDesk/sdServices/agent-action/trigger-contract";
import { CustomKeyValues } from "../../logger/telemetry-service";
import { UiControlConstants } from "../../common";
import { SdServicesSharedEnums } from "../../../ServiceDesk/sdServices/sdServices-shared-enums";
import { FlightingService } from "../../../ServiceDesk/sdServices//flightingSvc";

/** The bindings for multi action control
 *  The properties (actionContext | name | restrictedUXTypes | requiredRights) should be in sync with ServiceDesk.Services.AgentAction.ActionTriggerOptions
 */
export class MultiActionBinding {
  /** The context to be submitted to the action implementation to evaluate eligibility and provide execution state. This is not strongly typed as the action trigger directive will not be able to infer the action context type.*/
  public actionContext: any = "&";

  /** The name of the action to trigger, used to connect action triggers to action implementations. These names should be scoped, such that an action initiated from a specific codebase would be unique, e.g. "MST_CaseCreate". */
  public name = "@";

  /** A list of UXTypes which are not allowed when invoking implementations for the current action trigger.
   *  Should be of type ServiceDesk.Services.AgentAction.ActionUXType[]
   */
  public restrictedUxTypes = "@?";

  /** The list of required rights for the action to be triggered.
   *  Should be of ServiceDesk.Services.AgentAction. RightRequirement[]
   */
  public requiredRights = "@?";

  /** true/false true indicates the alignment of flyout to be right. defaults to left*/
  public alignRight = "<?";

  /** The metaTags for loggind awa event telemetry, includes scenario and scenario steps */
  public metaTags = "<";

  public telemetryData = "<";

  public ariaLabel = "@?";
}

export class MultiActionController {
  public actions: any;
  public actionContext: any;
  public name: string;
  public restrictedUxTypes: any[];
  public requiredRights: RightRequirement[];
  public alignRight: boolean;
  public metaTags: CustomKeyValues;
  public actionTriggerLinks: ActionTriggerLink[] = [];
  public hideControl = false;
  public showProgressIndicator = true;
  public showProgressIndicatorFlight = false;
  // @ngInject
  constructor(
    private $scope: ng.IScope,
    private $element: ng.IAugmentedJQuery,
    private actionRegistryService: ActionRegistry,
    private $timeout: ng.ITimeoutService,
    private uiControlConstants: UiControlConstants,
    private flightingService: FlightingService,
    private sdServicesSharedEnums: SdServicesSharedEnums
  ) {}

  public $onInit() {
    this.showProgressIndicatorFlight = this.flightingService.isFlightEnabled(
      "CustomerTab_CST_EnableDownloadProgressDisplay"
    );
    this.actionTriggerLinks = this.actionRegistryService.getMultiActionTriggerLinks(
      this.getAttributeOptions(),
      this.$element
    );
    if (this.actionTriggerLinks) {
      this.actionTriggerLinks.forEach((triggerLink: ActionTriggerLink) => {
        let actionEligibilityWatcher = this.$scope.$watch(
          () => {
            return triggerLink.getActionEligibility();
          },
          (viewState: any, oldViewState: any) => {
            this.checkIfActionsAreUnknown(actionEligibilityWatcher);
          }
        );
      });
    } else {
      // hide the control when no actions was found registered for the actionTriggerName
      this.showProgressIndicator = false;
      this.hideControl = true;
    }
  }

  public getAttributeOptions(name?: string): ActionTriggerOptions {
    return {
      actionContext: this.actionContext,
      name: name || this.name,
      restrictedUXTypes: this.restrictedUxTypes,
      requiredRights: this.requiredRights,
      showProgress: (isInProgress: boolean): void => {
        if (this.showProgressIndicatorFlight) {
          this.showProgressIndicator = !!isInProgress;
        }
      }
    };
  }

  private checkIfActionsAreUnknown(actionEligibilityWatcher: () => void) {
    let showProgressIndicator = true;
    this.actionTriggerLinks.forEach((triggerLink: ActionTriggerLink) => {
      if (!this.checkIfActionEligibilityIsYetToBeKnown(triggerLink.getActionEligibility())) {
        showProgressIndicator = false;
        // deregister the watcher on action eligibility since we got atleast one action whose eligibility is well defined or NotYetKnown
        actionEligibilityWatcher();
      }
    });

    this.showProgressIndicator = showProgressIndicator;
  }

  public checkIfActionEligibilityIsYetToBeKnown(eligibility: any): boolean {
    if (
      eligibility == this.sdServicesSharedEnums.actionEligibility.Default ||
      eligibility == this.sdServicesSharedEnums.actionEligibility.NotAllowed ||
      eligibility == this.sdServicesSharedEnums.actionEligibility.NotApplicable
    ) {
      return false;
    }
    return true;
  }

  public invokeAction(triggerLink: ActionTriggerLink): boolean {
    if (triggerLink.getActionEligibility() == this.sdServicesSharedEnums.actionEligibility.Default) {
      triggerLink.invoke();
      return true;
    }

    return false;
  }

  public getActionUiState(actionTriggerlink: ActionTriggerLink): any {
    if (actionTriggerlink.getActionEligibility() == this.sdServicesSharedEnums.actionEligibility.NotAllowed) {
      return this.uiControlConstants.multiActionUiState.NotAllowed;
    }

    if (actionTriggerlink.getActionEligibility() == this.sdServicesSharedEnums.actionEligibility.Default) {
      return this.uiControlConstants.multiActionUiState.Valid;
    }

    if (this.checkIfActionEligibilityIsYetToBeKnown(actionTriggerlink.getActionEligibility())) {
      return this.uiControlConstants.multiActionUiState.NotKnown;
    }
  }
}
export const sdMultiAction: ng.IComponentOptions = {
  bindings: <any>new MultiActionBinding(),
  controller: MultiActionController,
  templateUrl: "../ShellCommon/Controls/multi-action/multi-action.html"
};
